import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileText, Edit3, Trash2, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";

interface Document {
  id: string;
  title: string;
  type: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface AllDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentSelect: (documentId: string) => void;
  onDocumentDelete: (documentId: string, event?: React.MouseEvent) => void;
  onDocumentRename: (doc: Document, event: React.MouseEvent) => void;
  editingDocumentId: string | null;
  editingTitle: string;
  setEditingTitle: (title: string) => void;
  onRenameDocument: (documentId: string) => void;
  onCancelRename: () => void;
}

const DOCUMENTS_PER_PAGE = 12;

const AllDocumentsModal: React.FC<AllDocumentsModalProps> = ({
  isOpen,
  onClose,
  onDocumentSelect,
  onDocumentDelete,
  onDocumentRename,
  editingDocumentId,
  editingTitle,
  setEditingTitle,
  onRenameDocument,
  onCancelRename
}) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>();

  // Load documents function
  const loadDocuments = useCallback(async (pageNum: number, search: string = "", reset: boolean = false) => {
    if (!user) return;
    
    console.log('🔍 Loading documents:', { pageNum, search, reset, isLoading, userId: user.id });
    
    setIsLoading(true);

    try {
      let query = supabase
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .range(pageNum * DOCUMENTS_PER_PAGE, (pageNum + 1) * DOCUMENTS_PER_PAGE - 1);

      if (search.trim()) {
        query = query.or(`title.ilike.%${search}%,type.ilike.%${search}%`);
      }

      console.log('📊 Executing query...');
      const { data, error } = await query;

      if (error) {
        console.error('❌ Error loading documents:', error);
        return;
      }

      const newDocuments = data || [];
      console.log('✅ Documents loaded:', newDocuments.length);
      
      if (reset) {
        setDocuments(newDocuments);
      } else {
        setDocuments(prev => [...prev, ...newDocuments]);
      }

      setHasMore(newDocuments.length === DOCUMENTS_PER_PAGE);
    } catch (error) {
      console.error('❌ Error loading documents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initial load when modal opens
  useEffect(() => {
    if (isOpen && user) {
      console.log('🚀 Modal opened, initializing...');
      setDocuments([]);
      setPage(0);
      setSearchTerm("");
      setHasMore(true);
      loadDocuments(0, "", true);
    }
  }, [isOpen, user?.id]);

  // Search effect
  useEffect(() => {
    if (!isOpen || !user) return;
    
    // Não disparar se searchTerm está vazio (estado inicial)
    if (searchTerm === "") return;

    console.log('🔍 Search term changed:', searchTerm);
    const debounceTimer = setTimeout(() => {
      setDocuments([]);
      setPage(0);
      setHasMore(true);
      loadDocuments(0, searchTerm, true);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen]);

  // DEBUG: Scroll validation - TEMPORÁRIO PARA DIAGNÓSTICO
  useEffect(() => {
    if (isOpen && documents.length > 0) {
      setTimeout(() => {
        const content  = document.querySelector('[role="dialog"] [data-dialog-content]') as HTMLElement;
        const root     = document.querySelector('[role="dialog"] [data-scroll-root]') as HTMLElement;
        const viewport = document.querySelector('[role="dialog"] [data-radix-scroll-area-viewport]') as HTMLElement;

        console.log('DEBUG MODAL =>', {
          content: {
            className: content?.className,
            clientHeight: content?.clientHeight,
            scrollHeight: content?.scrollHeight,
            overflowY: content ? getComputedStyle(content).overflowY : null,
            display: content ? getComputedStyle(content).display : null,
          },
          root: {
            className: root?.className,
            clientHeight: root?.clientHeight,
            scrollHeight: root?.scrollHeight,
            overflowY: root ? getComputedStyle(root).overflowY : null,
          },
          viewport: {
            className: viewport?.className,
            clientHeight: viewport?.clientHeight,
            scrollHeight: viewport?.scrollHeight,
            overflowY: viewport ? getComputedStyle(viewport).overflowY : null,
          }
        });

        // Ajuda a localizar qualquer "h-[500px]" residual no DOM:
        const any500 = Array.from(document.querySelectorAll('[role="dialog"] *'))
          .filter(el => (el as HTMLElement).className?.toString?.().includes('h-[500px]'))
          .map(el => (el as HTMLElement).className);
        console.log('NODES COM h-[500px]:', any500);
      }, 150);
    }
  }, [isOpen, documents.length]);

  // Intersection Observer for infinite scroll
  const lastDocumentElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoading) {
        console.log('🔄 Loading more documents...');
        const nextPage = page + 1;
        setPage(nextPage);
        loadDocuments(nextPage, searchTerm);
      }
    });

    if (node) observerRef.current.observe(node);
  }, [isLoading, hasMore, page, searchTerm]);

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[...Array(6)].map((_, i) => (
        <Card key={i} className="p-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-4 w-4 mt-1" />
            <div className="flex-1">
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-3 w-20 mb-1" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 flex flex-col">{/* padrão legal aplicado */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">
            Todos os Documentos
          </DialogTitle>
          <DialogDescription>
            Lista completa de todos os seus documentos com busca e paginação
          </DialogDescription>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar documentos..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              autoFocus={false}
            />
          </div>
        </DialogHeader>

        <ScrollArea ref={scrollAreaRef} className="flex-1 min-h-0 max-h-[65vh] overflow-y-auto px-6 pb-6">{/* padrão legal exato */}
          <div className="space-y-4">
            {documents.length === 0 && !isLoading ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum documento encontrado</h3>
                <p className="text-muted-foreground">
                  {searchTerm 
                    ? "Tente ajustar os termos de busca" 
                    : "Você ainda não possui documentos"
                  }
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc, index) => (
                  <Card
                    key={doc.id}
                    ref={index === documents.length - 1 ? lastDocumentElementRef : null}
                    className="cursor-pointer relative group hover:shadow-md transition-shadow"
                    onClick={() => {
                      if (editingDocumentId !== doc.id) {
                        onDocumentSelect(doc.id);
                        onClose();
                      }
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 pr-16">
                        <FileText className="h-4 w-4" />
                        {editingDocumentId === doc.id ? (
                          <Input
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                onRenameDocument(doc.id);
                              }
                              if (e.key === 'Escape') {
                                onCancelRename();
                              }
                            }}
                            onBlur={() => onRenameDocument(doc.id)}
                            className="h-6 text-sm"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          doc.title
                        )}
                      </CardTitle>

                      {/* Action Buttons */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => onDocumentRename(doc, e)}
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir o documento "{doc.title}"?
                                Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={(e) => onDocumentDelete(doc.id, e)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-2 text-muted-foreground">
                        Tipo: {doc.type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Atualizado em {new Date(doc.updated_at).toLocaleDateString('pt-BR')}
                      </p>
                    </CardContent>
                  </Card>
                ))}

                {/* Loading more indicator */}
                {isLoading && <LoadingSkeleton />}
              </div>
            )}

            {/* End of results indicator */}
            {documents.length > 0 && !hasMore && !isLoading && (
              <div className="text-center py-4">
                <p className="text-muted-foreground text-sm">
                  {documents.length} documento{documents.length !== 1 ? 's' : ''} carregado{documents.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default AllDocumentsModal;